import React, { FC, useMemo } from 'react';
import { IFormItemType } from '@interfaces/FormItemType';
import { useTranslate } from '@translations/useTranslate';
import { Translations } from '@translations/translationsTypes';
import styles from './CustomRadio.module.scss';

interface CustomRadioProps {
  formItem: IFormItemType;
  inline?: boolean;
}

export const CustomRadio: FC<CustomRadioProps> = ({ formItem, inline = false }) => {
  const { language } = useTranslate();

  const {
    label,
    id,
    value,
    RadioOptions,
    required,
    errorFeedback,
    isTouched,
    onChange,
    disabled = false,
  } = formItem;

  const hasError = isTouched && errorFeedback;

  const handleChange = (selectedValue: string | number) => {
    onChange(selectedValue);
  };

  const options = useMemo(() => {
    return RadioOptions?.map((item) => ({
      ...item,
      displayLabel: language === Translations.ar ? item.labelAr ?? item.label : item.label,
    }));
  }, [language, RadioOptions]);

  return (
    <div
      className={`${styles.radio_group} ${hasError ? styles.has_error : ''} ${
        inline ? styles.inline : ''
      }`}
    >
      {label && (
        <div className={styles.radio_group__label}>
          {required && <span className={styles.radio_group__label_required}> *</span>}
          {label}
        </div>
      )}

      <div
        className={`${styles.radio_group__options} ${inline ? styles.horizontal : styles.vertical}`}
      >
        {options?.map((option, index) => (
          <label
            key={`${id}-${index}`}
            className={`${styles.radio_group__option} ${
              value === option.value ? styles.selected : ''
            }`}
          >
            <input
              type="radio"
              id={`${id}-${index}`}
              name={id}
              value={option.value}
              checked={value === option.value}
              onChange={() => handleChange(option.value)}
              disabled={disabled}
              className={styles.radio_group__input}
            />
            <span className={styles.radio_group__custom}></span>
            <span className={styles.radio_group__text}>{option.displayLabel}</span>
          </label>
        ))}
      </div>

      {hasError && (
        <div className={`${styles.radio_group__feedback} invalid-feedback d-block`}>
          {errorFeedback}
        </div>
      )}
    </div>
  );
};
